import React, { useState, useEffect, useCallback } from 'react';

interface NumberInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value' | 'type'
> {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  fallback?: number;
}

/**
 * A number input that lets the user type freely without immediate clamping.
 * Values are validated and committed on blur or Enter.
 * Arrow / stepper buttons still work instantly via the native input behaviour.
 */
export const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  min,
  max,
  fallback,
  className,
  ...rest
}) => {
  const [localValue, setLocalValue] = useState(String(value));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setLocalValue(String(value));
    }
  }, [value, isFocused]);

  const commit = useCallback(
    (raw: string) => {
      let parsed = parseInt(raw, 10);
      if (isNaN(parsed)) {
        parsed = fallback ?? value;
      }
      if (min !== undefined) parsed = Math.max(parsed, min);
      if (max !== undefined) parsed = Math.min(parsed, max);
      setLocalValue(String(parsed));
      onChange(parsed);
    },
    [onChange, min, max, fallback, value]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setLocalValue(raw);

    // Native stepper arrows produce complete valid values — commit immediately
    // so the user gets instant feedback from the arrows.
    // We detect this by checking if the value is a valid integer within range.
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed) && String(parsed) === raw.trim()) {
      if ((min === undefined || parsed >= min) && (max === undefined || parsed <= max)) {
        onChange(parsed);
      }
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    commit(localValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commit(localValue);
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <input
      type="number"
      value={localValue}
      onChange={handleChange}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      min={min}
      max={max}
      className={className}
      {...rest}
    />
  );
};
